import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import ScoutStage from "./ScoutStage.jsx";
import LayersStage from "./LayersStage.jsx";
import VentureStage from "./VentureStage.jsx";
import DebriefStage from "./DebriefStage.jsx";
import "./MoatRunner.css";

const STAGES = ["intro", "scout", "layers", "venture", "debrief"];
const STAGE_LABELS = { scout: "Pitch Floor", layers: "AI Layers", venture: "The Venture", debrief: "Empire Report" };

export default function MoatRunner() {
  const navigate = useNavigate();
  const [stage, setStage] = useState("intro");
  const [scoutResult, setScoutResult] = useState(null);
  const [layersResult, setLayersResult] = useState(null);
  const [ventureResult, setVentureResult] = useState(null);

  const restart = () => {
    setScoutResult(null);
    setLayersResult(null);
    setVentureResult(null);
    setStage("intro");
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-8">
      <button onClick={() => navigate("/")} className="btn-ghost mb-6 !text-[8px]">
        ◀ BACK TO LEVELS
      </button>

      <div className="panel relative overflow-hidden p-6">
        <div className="mb-6">
          <p className="label-pixel text-magenta">Level 10</p>
          <h1 className="pixel-heading text-base text-mist sm:text-xl">Moat Runner</h1>
        </div>

        {stage !== "intro" && (
          <div className="mb-6 flex gap-1.5">
            {["scout", "layers", "venture", "debrief"].map((s) => {
              const idx = STAGES.indexOf(s);
              const currentIdx = STAGES.indexOf(stage);
              const complete = idx < currentIdx;
              const active = s === stage;
              return (
                <div key={s} className="flex-1">
                  <div
                    className={`h-1.5 w-full border border-black/60 ${
                      complete ? "bg-lime" : active ? "bg-cyan" : "bg-panel2"
                    }`}
                  />
                  <p
                    className={`mt-1 text-center font-display text-[7px] uppercase ${
                      active ? "text-cyan" : complete ? "text-lime" : "text-mist/30"
                    }`}
                  >
                    {STAGE_LABELS[s]}
                  </p>
                </div>
              );
            })}
          </div>
        )}

        <div className="mr-panel-transition" key={stage}>
          {stage === "intro" && (
            <div className="text-center">
              <p className="mb-3 text-sm text-mist/80">
                Everyone on your feed is chasing the same flashy startup pitch this week. Meanwhile
                somebody's quietly getting <span className="text-cyan">paid twice</span> to pump
                grease out of a restaurant's kitchen. Real wealth doesn't always look impressive —
                sometimes it looks like a clipboard, a code inspection, and a
                <span className="text-magenta"> contract nobody else wanted to read</span>.
              </p>
              <p className="mb-6 text-xs text-mist/50">
                Three phases: read the pitch floor for the actual moat behind a business most
                people would walk straight past, sort out where the AI buildout genuinely pays
                right now, then run a venture through the decisions that decide whether a boring
                bet turns into an empire — or a window that quietly closed.
              </p>
              <button onClick={() => setStage("scout")} className="btn-magenta">
                ▶ HIT THE FLOOR
              </button>
            </div>
          )}

          {stage === "scout" && (
            <ScoutStage
              onComplete={(result) => {
                setScoutResult(result);
                setStage("layers");
              }}
            />
          )}

          {stage === "layers" && (
            <LayersStage
              onComplete={(result) => {
                setLayersResult(result);
                setStage("venture");
              }}
            />
          )}

          {stage === "venture" && scoutResult && layersResult && (
            <VentureStage
              onComplete={(result) => {
                setVentureResult(result);
                setStage("debrief");
              }}
            />
          )}

          {stage === "debrief" && scoutResult && layersResult && ventureResult && (
            <DebriefStage
              scoutResult={scoutResult}
              layersResult={layersResult}
              ventureResult={ventureResult}
              onRetry={restart}
            />
          )}
        </div>
      </div>
    </div>
  );
}
